import type { AppColors } from './palettes';

/** User-selectable accent palettes. `default` leaves the base GoalMint palette unchanged. */
export type AccentColorId =
  | 'default'
  | 'purple'
  | 'pink'
  | 'royalBlue'
  | 'navy'
  | 'orange'
  | 'teal'
  | 'red'
  | 'amber'
  | 'indigo';

export type AccentColorOption = {
  id: AccentColorId;
  label: string;
  swatch: string;
};

export const ACCENT_COLOR_OPTIONS: AccentColorOption[] = [
  { id: 'default', label: 'GoalMint', swatch: '#166534' },
  { id: 'purple', label: 'Purple', swatch: '#7C3AED' },
  { id: 'pink', label: 'Pink', swatch: '#DB2777' },
  { id: 'royalBlue', label: 'Royal Blue', swatch: '#4169E1' },
  { id: 'navy', label: 'Navy', swatch: '#1E3A8A' },
  { id: 'orange', label: 'Orange', swatch: '#EA580C' },
  { id: 'teal', label: 'Teal', swatch: '#0D9488' },
  { id: 'red', label: 'Red', swatch: '#DC2626' },
  { id: 'amber', label: 'Amber', swatch: '#D97706' },
  { id: 'indigo', label: 'Indigo', swatch: '#4F46E5' },
];

type AccentOverrides = {
  primaryMain: string;
  primaryLight: string;
  buttonPrimary: string;
  authCta: string;
  navActiveText: string;
  accentMint: string;
  accentMintDark: string;
  dashboardIncomeCardBorderDark: string;
};

const ACCENT_OVERRIDES: Record<
  Exclude<AccentColorId, 'default'>,
  { light: AccentOverrides; dark: AccentOverrides }
> = {
  purple: {
    light: {
      primaryMain: '#6D28D9',
      primaryLight: '#7C3AED',
      buttonPrimary: '#6D28D9',
      authCta: '#6D28D9',
      navActiveText: '#A78BFA',
      accentMint: '#A78BFA',
      accentMintDark: '#7C3AED',
      dashboardIncomeCardBorderDark: '#A78BFA',
    },
    dark: {
      primaryMain: '#7C3AED',
      primaryLight: '#6D28D9',
      buttonPrimary: '#7C3AED',
      authCta: '#7C3AED',
      navActiveText: '#C4B5FD',
      accentMint: '#C4B5FD',
      accentMintDark: '#A78BFA',
      dashboardIncomeCardBorderDark: '#C4B5FD',
    },
  },
  pink: {
    light: {
      primaryMain: '#BE185D',
      primaryLight: '#DB2777',
      buttonPrimary: '#BE185D',
      authCta: '#BE185D',
      navActiveText: '#F472B6',
      accentMint: '#F472B6',
      accentMintDark: '#DB2777',
      dashboardIncomeCardBorderDark: '#F472B6',
    },
    dark: {
      primaryMain: '#DB2777',
      primaryLight: '#BE185D',
      buttonPrimary: '#DB2777',
      authCta: '#DB2777',
      navActiveText: '#F9A8D4',
      accentMint: '#F9A8D4',
      accentMintDark: '#F472B6',
      dashboardIncomeCardBorderDark: '#F9A8D4',
    },
  },
  royalBlue: {
    light: {
      primaryMain: '#2563EB',
      primaryLight: '#4169E1',
      buttonPrimary: '#2563EB',
      authCta: '#2563EB',
      navActiveText: '#60A5FA',
      accentMint: '#60A5FA',
      accentMintDark: '#4169E1',
      dashboardIncomeCardBorderDark: '#60A5FA',
    },
    dark: {
      primaryMain: '#4169E1',
      primaryLight: '#2563EB',
      buttonPrimary: '#4169E1',
      authCta: '#4169E1',
      navActiveText: '#93C5FD',
      accentMint: '#93C5FD',
      accentMintDark: '#60A5FA',
      dashboardIncomeCardBorderDark: '#93C5FD',
    },
  },
  navy: {
    light: {
      primaryMain: '#1E3A8A',
      primaryLight: '#1D4ED8',
      buttonPrimary: '#1E3A8A',
      authCta: '#1E3A8A',
      navActiveText: '#93C5FD',
      accentMint: '#93C5FD',
      accentMintDark: '#3B82F6',
      dashboardIncomeCardBorderDark: '#93C5FD',
    },
    dark: {
      primaryMain: '#2563EB',
      primaryLight: '#1E3A8A',
      buttonPrimary: '#2563EB',
      authCta: '#2563EB',
      navActiveText: '#BFDBFE',
      accentMint: '#BFDBFE',
      accentMintDark: '#93C5FD',
      dashboardIncomeCardBorderDark: '#BFDBFE',
    },
  },
  orange: {
    light: {
      primaryMain: '#C2410C',
      primaryLight: '#EA580C',
      buttonPrimary: '#C2410C',
      authCta: '#C2410C',
      navActiveText: '#FB923C',
      accentMint: '#FB923C',
      accentMintDark: '#EA580C',
      dashboardIncomeCardBorderDark: '#FB923C',
    },
    dark: {
      primaryMain: '#EA580C',
      primaryLight: '#C2410C',
      buttonPrimary: '#EA580C',
      authCta: '#EA580C',
      navActiveText: '#FDBA74',
      accentMint: '#FDBA74',
      accentMintDark: '#FB923C',
      dashboardIncomeCardBorderDark: '#FDBA74',
    },
  },
  teal: {
    light: {
      primaryMain: '#0F766E',
      primaryLight: '#0D9488',
      buttonPrimary: '#0F766E',
      authCta: '#0F766E',
      navActiveText: '#2DD4BF',
      accentMint: '#2DD4BF',
      accentMintDark: '#0D9488',
      dashboardIncomeCardBorderDark: '#2DD4BF',
    },
    dark: {
      primaryMain: '#0D9488',
      primaryLight: '#0F766E',
      buttonPrimary: '#0D9488',
      authCta: '#0D9488',
      navActiveText: '#5EEAD4',
      accentMint: '#5EEAD4',
      accentMintDark: '#2DD4BF',
      dashboardIncomeCardBorderDark: '#5EEAD4',
    },
  },
  red: {
    light: {
      primaryMain: '#B91C1C',
      primaryLight: '#DC2626',
      buttonPrimary: '#B91C1C',
      authCta: '#B91C1C',
      navActiveText: '#F87171',
      accentMint: '#F87171',
      accentMintDark: '#DC2626',
      dashboardIncomeCardBorderDark: '#F87171',
    },
    dark: {
      primaryMain: '#DC2626',
      primaryLight: '#B91C1C',
      buttonPrimary: '#DC2626',
      authCta: '#DC2626',
      navActiveText: '#FCA5A5',
      accentMint: '#FCA5A5',
      accentMintDark: '#F87171',
      dashboardIncomeCardBorderDark: '#FCA5A5',
    },
  },
  amber: {
    light: {
      primaryMain: '#B45309',
      primaryLight: '#D97706',
      buttonPrimary: '#B45309',
      authCta: '#B45309',
      navActiveText: '#FBBF24',
      accentMint: '#FBBF24',
      accentMintDark: '#D97706',
      dashboardIncomeCardBorderDark: '#FBBF24',
    },
    dark: {
      primaryMain: '#D97706',
      primaryLight: '#B45309',
      buttonPrimary: '#D97706',
      authCta: '#D97706',
      navActiveText: '#FCD34D',
      accentMint: '#FCD34D',
      accentMintDark: '#FBBF24',
      dashboardIncomeCardBorderDark: '#FCD34D',
    },
  },
  indigo: {
    light: {
      primaryMain: '#4338CA',
      primaryLight: '#4F46E5',
      buttonPrimary: '#4338CA',
      authCta: '#4338CA',
      navActiveText: '#818CF8',
      accentMint: '#818CF8',
      accentMintDark: '#4F46E5',
      dashboardIncomeCardBorderDark: '#818CF8',
    },
    dark: {
      primaryMain: '#4F46E5',
      primaryLight: '#4338CA',
      buttonPrimary: '#4F46E5',
      authCta: '#4F46E5',
      navActiveText: '#A5B4FC',
      accentMint: '#A5B4FC',
      accentMintDark: '#818CF8',
      dashboardIncomeCardBorderDark: '#A5B4FC',
    },
  },
};

export function isAccentColorId(value: string): value is AccentColorId {
  return ACCENT_COLOR_OPTIONS.some((option) => option.id === value);
}

function applyOverrides(palette: AppColors, overrides: AccentOverrides): AppColors {
  return {
    ...palette,
    accent: {
      ...palette.accent,
      mint: overrides.accentMint,
      mintDark: overrides.accentMintDark,
    },
    nav: {
      ...palette.nav,
      activeText: overrides.navActiveText,
    },
    primary: {
      ...palette.primary,
      main: overrides.primaryMain,
      light: overrides.primaryLight,
    },
    auth: {
      ...palette.auth,
      cta: overrides.authCta,
    },
    button: {
      ...palette.button,
      primary: overrides.buttonPrimary,
    },
    tabBar: {
      ...palette.tabBar,
      active: overrides.buttonPrimary,
    },
    dashboard: {
      ...palette.dashboard,
      incomeCardBorderDark: overrides.dashboardIncomeCardBorderDark,
    },
  };
}

/** Apply accent color on top of the base palette. Default returns the palette unchanged. */
export function applyAccentColor(
  palette: AppColors,
  accentId: AccentColorId,
  mode: 'light' | 'dark'
): AppColors {
  if (accentId === 'default') {
    return palette;
  }

  const overrides = ACCENT_OVERRIDES[accentId][mode];
  return applyOverrides(palette, overrides);
}
